import React, { useState, useCallback } from 'react';
import TEAMS from '../data/teams';

// Basit grup + eleme turnuvası
function generateTournament(selectedTeams) {
  // 8 takım: 2 grup x 4 takım, sonra yarı final, final
  const shuffled = [...selectedTeams].sort(() => Math.random() - 0.5);
  const groupA = shuffled.slice(0, 4);
  const groupB = shuffled.slice(4, 8);

  return {
    type: 'tournament',
    groups: {
      A: groupA.map(t => ({
        team: t,
        played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, points: 0
      })),
      B: groupB.map(t => ({
        team: t,
        played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, points: 0
      })),
    },
    phase: 'group', // group | semi | final | finished
    groupMatches: generateGroupMatches(groupA, groupB),
    currentMatchIndex: 0,
    knockoutMatches: [],
    champion: null,
  };
}

function generateGroupMatches(groupA, groupB) {
  const matches = [];
  // Her takım birbirine karşı 1 maç
  for (let i = 0; i < groupA.length; i++) {
    for (let j = i + 1; j < groupA.length; j++) {
      matches.push({ home: groupA[i], away: groupA[j], group: 'A', played: false, homeScore: 0, awayScore: 0 });
    }
  }
  for (let i = 0; i < groupB.length; i++) {
    for (let j = i + 1; j < groupB.length; j++) {
      matches.push({ home: groupB[i], away: groupB[j], group: 'B', played: false, homeScore: 0, awayScore: 0 });
    }
  }
  return matches;
}

function getStandings(group) {
  return [...group].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    const gdA = a.gf - a.ga;
    const gdB = b.gf - b.ga;
    if (gdB !== gdA) return gdB - gdA;
    return b.gf - a.gf;
  });
}

export default function Tournament({ userTeam, role, onStartMatch, onBack }) {
  const [selectedTeams, setSelectedTeams] = useState([userTeam]);
  const [tournament, setTournament] = useState(null);
  const [phase, setPhase] = useState('setup'); // setup | bracket | playing

  const toggleTeam = (team) => {
    if (team.id === userTeam.id) return;
    setSelectedTeams(prev => {
      const exists = prev.find(t => t.id === team.id);
      if (exists) return prev.filter(t => t.id !== team.id);
      if (prev.length >= 8) return prev;
      return [...prev, team];
    });
  };

  const startTournament = () => {
    if (selectedTeams.length !== 8) return;
    const t = generateTournament(selectedTeams);
    setTournament(t);
    setPhase('bracket');
  };

  const handlePlayMatch = useCallback((match) => {
    const isUserMatch = match.home.id === userTeam.id || match.away.id === userTeam.id;
    if (isUserMatch) {
      // Kullanıcı bu maçı oynar
      const isHome = match.home.id === userTeam.id;
      onStartMatch(
        isHome ? match.home : match.away,
        isHome ? match.away : match.home,
        (homeScore, awayScore) => {
          applyMatchResult(match, isHome ? homeScore : awayScore, isHome ? awayScore : homeScore);
        }
      );
    } else {
      // AI simülasyonu
      simulateMatch(match);
    }
  }, [userTeam, onStartMatch]);

  const simulateMatch = (match) => {
    const homeStr = match.home.rating;
    const awayStr = match.away.rating;
    const total = homeStr + awayStr;
    let homeGoals = 0, awayGoals = 0;

    for (let i = 0; i < 8; i++) {
      if (Math.random() < homeStr / total * 0.35) homeGoals++;
      if (Math.random() < awayStr / total * 0.35) awayGoals++;
    }

    applyMatchResult(match, homeGoals, awayGoals);
  };

  const applyMatchResult = (match, homeScore, awayScore) => {
    setTournament(prev => {
      const t = JSON.parse(JSON.stringify(prev));
      const m = t.phase === 'group'
        ? t.groupMatches.find(gm => gm.home.id === match.home.id && gm.away.id === match.away.id && !gm.played)
        : t.knockoutMatches.find(km => km.home.id === match.home.id && km.away.id === match.away.id && !km.played);

      if (!m) return t;

      m.played = true;
      m.homeScore = homeScore;
      m.awayScore = awayScore;

      if (t.phase === 'group') {
        // Grup tablosu güncelle
        const group = t.groups[m.group];
        const homeEntry = group.find(e => e.team.id === m.home.id);
        const awayEntry = group.find(e => e.team.id === m.away.id);

        homeEntry.played++;
        awayEntry.played++;
        homeEntry.gf += homeScore;
        homeEntry.ga += awayScore;
        awayEntry.gf += awayScore;
        awayEntry.ga += homeScore;

        if (homeScore > awayScore) {
          homeEntry.won++; homeEntry.points += 3;
          awayEntry.lost++;
        } else if (awayScore > homeScore) {
          awayEntry.won++; awayEntry.points += 3;
          homeEntry.lost++;
        } else {
          homeEntry.drawn++; homeEntry.points += 1;
          awayEntry.drawn++; awayEntry.points += 1;
        }

        // Tüm grup maçları bitti mi?
        const allPlayed = t.groupMatches.every(gm => gm.played);
        if (allPlayed) {
          const standA = getStandings(t.groups.A);
          const standB = getStandings(t.groups.B);
          t.knockoutMatches = [
            { home: standA[0].team, away: standB[1].team, played: false, homeScore: 0, awayScore: 0, round: 'semi' },
            { home: standB[0].team, away: standA[1].team, played: false, homeScore: 0, awayScore: 0, round: 'semi' },
          ];
          t.phase = 'semi';
        }
      } else if (t.phase === 'semi') {
        const allSemiPlayed = t.knockoutMatches.filter(km => km.round === 'semi').every(km => km.played);
        if (allSemiPlayed) {
          const semis = t.knockoutMatches.filter(km => km.round === 'semi');
          const winner1 = semis[0].homeScore >= semis[0].awayScore ? semis[0].home : semis[0].away;
          const winner2 = semis[1].homeScore >= semis[1].awayScore ? semis[1].home : semis[1].away;
          t.knockoutMatches.push({
            home: winner1, away: winner2, played: false, homeScore: 0, awayScore: 0, round: 'final'
          });
          t.phase = 'final';
        }
      } else if (t.phase === 'final') {
        const finalM = t.knockoutMatches.find(km => km.round === 'final');
        if (finalM && finalM.played) {
          t.champion = finalM.homeScore >= finalM.awayScore ? finalM.home : finalM.away;
          t.phase = 'finished';
        }
      }

      return t;
    });
  };

  // Tüm oyanmamış maçları sırayla simüle et (AI maçları)
  const simulateAllAIMatches = () => {
    if (!tournament) return;
    const matches = tournament.phase === 'group' ? tournament.groupMatches : tournament.knockoutMatches;
    const nextUnplayed = matches.find(m => !m.played);
    if (nextUnplayed) {
      const isUserMatch = nextUnplayed.home.id === userTeam.id || nextUnplayed.away.id === userTeam.id;
      if (!isUserMatch) {
        simulateMatch(nextUnplayed);
      }
    }
  };

  // Setup ekranı
  if (phase === 'setup') {
    return (
      <div className="tournament">
        <div className="trn-header">
          <button className="ts-back" onClick={onBack}>← Geri</button>
          <h1>🏆 TURNUVA</h1>
          <p className="trn-subtitle">8 takım seç (senin takımın: {userTeam.shortName})</p>
          <p className="trn-count">{selectedTeams.length} / 8 takım seçildi</p>
        </div>

        <div className="ts-teams-grid">
          {TEAMS.map(team => (
            <button
              key={team.id}
              className={`ts-team-card ${selectedTeams.find(t => t.id === team.id) ? 'selected-home' : ''} ${team.id === userTeam.id ? 'user-team' : ''}`}
              onClick={() => toggleTeam(team)}
              disabled={team.id === userTeam.id}
            >
              <div className="ts-card-badge" style={{
                background: team.colors.primary,
                color: team.colors.secondary,
                border: `2px solid ${team.colors.secondary}`
              }}>
                {team.shortName}
              </div>
              <span className="ts-card-name">{team.name}</span>
              <span className="ts-card-rating">{team.rating}</span>
            </button>
          ))}
        </div>

        <div className="ts-actions">
          <button className="ts-reset" onClick={() => setSelectedTeams([userTeam])}>Sıfırla</button>
          {selectedTeams.length === 8 && (
            <button className="ts-start" onClick={startTournament}>🏆 TURNUVAYI BAŞLAT</button>
          )}
        </div>
      </div>
    );
  }

  // Turnuva bracket ekranı
  if (phase === 'bracket' && tournament) {
    const currentPhase = tournament.phase;
    const standingsA = getStandings(tournament.groups.A);
    const standingsB = getStandings(tournament.groups.B);

    // Sonraki oynanacak maç
    let nextMatch = null;
    if (currentPhase === 'group') {
      nextMatch = tournament.groupMatches.find(m => !m.played);
    } else if (currentPhase === 'semi' || currentPhase === 'final') {
      nextMatch = tournament.knockoutMatches.find(m => !m.played);
    }

    const isUserNextMatch = nextMatch && (nextMatch.home.id === userTeam.id || nextMatch.away.id === userTeam.id);

    return (
      <div className="tournament">
        <div className="trn-header">
          <button className="ts-back" onClick={onBack}>← Geri</button>
          <h1>🏆 TURNUVA</h1>
          <p className="trn-phase">
            {currentPhase === 'group' ? 'GRUP AŞAMASI' :
             currentPhase === 'semi' ? 'YARI FİNAL' :
             currentPhase === 'final' ? 'FİNAL' : ''}
          </p>
        </div>

        {tournament.phase === 'finished' && (
          <div className="trn-champion">
            <h2>🏆 ŞAMPİYON</h2>
            <div className="trn-champion-badge" style={{
              background: tournament.champion.colors.primary,
              color: tournament.champion.colors.secondary
            }}>
              {tournament.champion.shortName}
            </div>
            <p>{tournament.champion.name}</p>
            <button className="ts-start" onClick={onBack} style={{marginTop: 20}}>ANA MENÜYE DÖN</button>
          </div>
        )}

        {/* Grup tabloları */}
        {(currentPhase === 'group' || currentPhase === 'semi' || currentPhase === 'final' || currentPhase === 'finished') && (
          <div className="trn-groups">
            {['A', 'B'].map(g => {
              const standings = g === 'A' ? standingsA : standingsB;
              return (
                <div key={g} className="trn-group">
                  <h3>Grup {g}</h3>
                  <table className="trn-table">
                    <thead>
                      <tr><th>Takım</th><th>O</th><th>G</th><th>B</th><th>M</th><th>AG</th><th>YG</th><th>P</th></tr>
                    </thead>
                    <tbody>
                      {standings.map((entry, i) => (
                        <tr key={entry.team.id} className={`${i < 2 ? 'qualified' : ''} ${entry.team.id === userTeam.id ? 'user-row' : ''}`}>
                          <td className="trn-team-cell">
                            <span className="trn-mini-badge" style={{
                              background: entry.team.colors.primary,
                              color: entry.team.colors.secondary
                            }}>{entry.team.shortName}</span>
                          </td>
                          <td>{entry.played}</td>
                          <td>{entry.won}</td>
                          <td>{entry.drawn}</td>
                          <td>{entry.lost}</td>
                          <td>{entry.gf}</td>
                          <td>{entry.ga}</td>
                          <td className="trn-points">{entry.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}

        {/* Maç sonuçları */}
        <div className="trn-matches">
          <h3>{currentPhase === 'group' ? 'Grup Maçları' : 'Eleme Maçları'}</h3>
          {(currentPhase === 'group' ? tournament.groupMatches : tournament.knockoutMatches).map((m, i) => (
            <div key={i} className={`trn-match ${m.played ? 'played' : ''} ${!m.played && nextMatch === m ? 'next' : ''}`}>
              <span className="trn-match-home" style={{color: m.home.colors.primary === '#FFFFFF' ? '#333' : m.home.colors.primary}}>
                {m.home.shortName}
              </span>
              <span className="trn-match-score">
                {m.played ? `${m.homeScore} - ${m.awayScore}` : 'vs'}
              </span>
              <span className="trn-match-away" style={{color: m.away.colors.primary === '#FFFFFF' ? '#333' : m.away.colors.primary}}>
                {m.away.shortName}
              </span>
              {m.group && <span className="trn-match-group">({m.group})</span>}
              {m.round && <span className="trn-match-round">{m.round === 'semi' ? 'YF' : 'FN'}</span>}
            </div>
          ))}
        </div>

        {/* Sonraki maç aksiyonu */}
        {nextMatch && !tournament.phase === 'finished' && (
          <div className="trn-next-action">
            {isUserNextMatch ? (
              <button className="ts-start" onClick={() => handlePlayMatch(nextMatch)}>
                ⚽ MAÇI OYNA ({nextMatch.home.shortName} vs {nextMatch.away.shortName})
              </button>
            ) : (
              <button className="ts-start" style={{background: 'linear-gradient(135deg, #3498db, #2980b9)'}}
                onClick={() => simulateAllAIMatches()}>
                ⏩ SİMÜLE ET ({nextMatch.home.shortName} vs {nextMatch.away.shortName})
              </button>
            )}
          </div>
        )}

        {nextMatch && tournament.phase !== 'finished' && (
          <div className="trn-next-action">
            {isUserNextMatch ? (
              <button className="ts-start" onClick={() => handlePlayMatch(nextMatch)}>
                ⚽ MAÇI OYNA ({nextMatch.home.shortName} vs {nextMatch.away.shortName})
              </button>
            ) : (
              <button className="ts-start" style={{background: 'linear-gradient(135deg, #3498db, #2980b9)'}}
                onClick={() => simulateAllAIMatches()}>
                ⏩ SİMÜLE ET ({nextMatch.home.shortName} vs {nextMatch.away.shortName})
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
}
